import type { CSSObject, SxProps, Theme } from '@mui/material/styles'

import { colorTokens } from '../../tokens/colors.ts'

export const GROUP_BORDER_RADIUS = 6
export const DEFAULT_TAB_MIN_WIDTH = 88
const TAB_HORIZONTAL_PADDING = 8
const TAB_VERTICAL_PADDING = 4

/**
 * Styles for the hidden-scrollbar container that wraps the tab buttons.
 */
export const getScrollContainerStyles = (isOverflowing: boolean): SxProps<Theme> => ({
  flex: '1 1 auto',
  minWidth: 0,
  overflowX: 'auto',
  overflowY: 'hidden',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  whiteSpace: 'nowrap',
  cursor: isOverflowing ? 'grab' : 'default',
  touchAction: 'pan-y',
  '&::-webkit-scrollbar': { display: 'none' }
})

const TAB_FOCUS_RING_COLOR = colorTokens.semantic.brand.primary

/**
 * Shared per-segment visual styles for tab and control buttons.
 * Pass `iconOnly` for arrow controls so the square hit-area padding from
 * {@link KestrixButton} is preserved.
 */
export const getSegmentStyles = (
  tabMinWidth: number,
  index: number,
  totalItems: number,
  isSelected: boolean,
  iconOnly: boolean = false
): SxProps<Theme> => {
  const isFirst = index === 0
  const isLast = index === totalItems - 1

  const paddingStyles: CSSObject = iconOnly
    ? {}
    : {
        paddingLeft: `${TAB_HORIZONTAL_PADDING}px`,
        paddingRight: `${TAB_HORIZONTAL_PADDING}px`,
        paddingTop: `${TAB_VERTICAL_PADDING}px`,
        paddingBottom: `${TAB_VERTICAL_PADDING}px`
      }

  return {
    minWidth: tabMinWidth,
    borderRadius: 0,
    marginLeft: isFirst ? 0 : '-1px',
    ...paddingStyles,
    position: 'relative',
    // Selected should always be higher than default,
    // Hover should always be higher than its current state.
    zIndex: isSelected ? 2 : 1,

    ...(isFirst && {
      borderTopLeftRadius: GROUP_BORDER_RADIUS,
      borderBottomLeftRadius: GROUP_BORDER_RADIUS
    }),
    ...(isLast && {
      borderTopRightRadius: GROUP_BORDER_RADIUS,
      borderBottomRightRadius: GROUP_BORDER_RADIUS
    }),

    '&:hover': {
      zIndex: isSelected ? 4 : 3 // Maintains the hierarchy
    },

    '&.Mui-focusVisible': {
      outline: 'none',
      boxShadow: `inset 0 0 0 2px ${TAB_FOCUS_RING_COLOR}`,
      zIndex: 5
    },

    ...(isSelected && {
      backgroundColor: colorTokens.buttonStates.secondary.selected.surface,
      borderColor: colorTokens.buttonStates.secondary.selected.border,
      color: colorTokens.buttonStates.secondary.selected.text,

      '&:hover, &:active': {
        backgroundColor: colorTokens.buttonStates.secondary.selected.surface,
        borderColor: colorTokens.buttonStates.secondary.selected.border,
        color: colorTokens.buttonStates.secondary.selected.text
      }
    })
  }
}
